// 本機截圖 / 錄影的純邏輯（APP#17）。
//
// 與瀏覽器 API（canvas / MediaRecorder / DOM 下載）解耦的可測純函式：
//   - captureFilename：deviceName + 時間戳 → 安全檔名
//   - pickRecorderMime：依瀏覽器支援度挑 MediaRecorder mimeType（優先 mp4）
//   - extFromMime / formatElapsed：副檔名與計時格式
// 真正碰 DOM/MediaRecorder 的副作用收在 useMediaCapture hook。

/** 兩位數補零。 */
function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * 檔名 = 裝置名 + 時間戳（例：`客廳_20260623-2312.png`）。
 * - 去除檔名不合法字元（/ \ : * ? " < > | 與控制字元），空白收斂為底線。
 * - deviceName 空白時退回 'capture'。
 * - 時間戳到「秒」（避免同一分鐘多次截圖覆蓋）：YYYYMMDD-HHmmss。
 */
export function captureFilename(deviceName: string, ext: string, date: Date = new Date()): string {
  const stamp =
    `${date.getFullYear()}${pad2(date.getMonth() + 1)}${pad2(date.getDate())}` +
    `-${pad2(date.getHours())}${pad2(date.getMinutes())}${pad2(date.getSeconds())}`;
  const safe = (deviceName || '')
    // eslint-disable-next-line no-control-regex
    .replace(/[/\\:*?"<>|\u0000-\u001f]/g, '')
    .trim()
    .replace(/\s+/g, '_');
  const base = safe || 'capture';
  const cleanExt = ext.replace(/^\./, '');
  return `${base}_${stamp}.${cleanExt}`;
}

/** 候選錄影 mimeType（優先 mp4，再 webm/vp9 → vp8 → 裸 webm）。 */
export const RECORDER_MIME_CANDIDATES: readonly string[] = [
  'video/mp4',
  'video/mp4;codecs=h264',
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
];

/** MediaRecorder 型別的最小介面（純函式測試用，避免依賴 jsdom 缺的全域）。 */
export interface MimeSupportProbe {
  isTypeSupported(type: string): boolean;
}

/**
 * 依瀏覽器支援度挑第一個可用的錄影 mimeType。
 * 全不支援（或無 MediaRecorder）回 null → 呼叫端顯示「不支援」提示。
 */
export function pickRecorderMime(
  probe: MimeSupportProbe | undefined =
    typeof MediaRecorder !== 'undefined' ? (MediaRecorder as unknown as MimeSupportProbe) : undefined,
  candidates: readonly string[] = RECORDER_MIME_CANDIDATES,
): string | null {
  if (!probe || typeof probe.isTypeSupported !== 'function') return null;
  for (const c of candidates) {
    try {
      if (probe.isTypeSupported(c)) return c;
    } catch {
      /* 個別型別查詢拋錯不致命，續試下一個 */
    }
  }
  return null;
}

/** mimeType → 下載副檔名（mp4 / webm；未知退回 webm）。 */
export function extFromMime(mime: string | null | undefined): string {
  if (!mime) return 'webm';
  if (mime.startsWith('video/mp4')) return 'mp4';
  return 'webm';
}

/** 經過秒數 → mm:ss（≥1 小時改 h:mm:ss）。錄影計時器顯示用。 */
export function formatElapsed(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${pad2(m)}:${pad2(sec)}`;
  return `${pad2(m)}:${pad2(sec)}`;
}
